import {
  state,
  exportDimensions,
  type Fps,
  type ViewMode,
  type AspectKey,
  type ResolutionKey,
  type VideoLayout,
} from '../state.ts';
import type { FingerTune } from '../retarget/fingerTune.ts';

export interface SidebarHandlers {
  onToggleGrid: (visible: boolean) => void;
  onFov: (fov: number) => void;
  onFps: (fps: Fps) => void;
  onViewMode: (mode: ViewMode) => void;
  onToggleActor: (id: string, visible: boolean) => void;
  onActorColor: (id: string, hex: string) => void;
  /** Load a VRM avatar for one specific actor (opens a file picker). */
  onLoadAvatar: (id: string) => void;
  /** Open the bone-mapping editor for an actor with a puppet. */
  onMapBones: (id: string) => void;
  /** "Adjust Actor Offset" controls (world offset in metres, scale ×1). */
  onAdjustChange: (id: string, patch: { x?: number; y?: number; z?: number; scale?: number }) => void;
  onAdjustReset: (id: string) => void;
  /** Record the actor's current offset/size as a key at the playhead. */
  onAdjustKey: (id: string) => void;
  onAdjustKeyDelete: (id: string, index: number) => void;
  /** Live evaluated adjust values (keyframed offsets animate during playback). */
  getAdjustLive: (id: string) => { x: number; y: number; z: number; scale: number } | null;
  /** Per-avatar finger offsets (VRM 0.x thumb fix + manual sliders). */
  onFingerTune: (id: string, patch: Partial<FingerTune>) => void;
  /** Record the actor's current finger tune as a key at the playhead. */
  onFingerKey: (id: string) => void;
  /** Live finger tune (keyframed tunes animate during playback). */
  getFingerTuneLive: (id: string) => FingerTune | null;
  /** "Fix broken fingers" healing strength (0..1). */
  onFingerFix: (strength: number) => void;
  onPreviewMesh: (which: 'xbot' | 'ybot') => void;
  onBackground: (hex: string) => void;
  onAspect: (key: AspectKey) => void;
  onResolution: (key: ResolutionKey) => void;
  onSaveScene: () => void;
  onLoadScene: (file: File) => void;
  onVideoLayout: (layout: VideoLayout) => void;
  onVideoDelta: (delta: number) => void;
  onVideoOpacity: (o: number) => void;
  onSceneOpacity: (o: number) => void;
  onVideoOnTop: (onTop: boolean) => void;
  onVideoKeyBg: (keyBackground: boolean) => void;
  onGroundShadow: (on: boolean) => void;
  onShadowOpacity: (opacity: number) => void;
  onLightAngles: (azimuth: number, elevation: number) => void;
  onMatchProportions: (on: boolean) => void;
  /** The Adjust-actor selection changed (may now be '', i.e. None). */
  onAdjustSelect: () => void;
  onVideoExportComposite: (on: boolean) => void;
  onRemoveVideo: () => void;
}

export interface ActorRow {
  id: string;
  label: string;
  kind: 'skeleton' | 'prop';
  visible: boolean;
  color: string | null;
  /** Name of the VRM avatar assigned to this actor, if any. */
  avatar: string | null;
  /** True when the actor has a puppet — enables the bone-mapping editor. */
  mappable: boolean;
  /** Current offset/size adjustment (shown in the Adjust panel). */
  adjust: { x: number; y: number; z: number; scale: number };
  /** Keyframed adjustments (listed under the Adjust panel). */
  adjustKeys: { time: number; x: number; y: number; z: number; scale: number }[];
  /** Finger tuning values (Adjust panel → Finger tuning). */
  fingerTune: FingerTune;
  /** Number of finger-tune keys (enables live slider reflection). */
  fingerKeyCount: number;
}

export interface FileMeta {
  name: string;
  bones: number;
  actors: number;
  frames: number;
  duration: number;
  hasMesh: boolean;
  /** Frame rate declared by (or estimated from) the file, if known. */
  nativeFps: number | null;
}

/** Zone 1 controls: file metadata, grid toggle, FOV slider, FPS toggle, view mode. */
export function createSidebar(handlers: SidebarHandlers) {
  const gridToggle = byId<HTMLInputElement>('grid-toggle');
  const shadowToggle = byId<HTMLInputElement>('shadow-toggle');
  const shadowOpacity = byId<HTMLInputElement>('shadow-opacity');
  const shadowOpacityValue = byId<HTMLOutputElement>('shadow-opacity-value');
  const lightAz = byId<HTMLInputElement>('light-az');
  const lightAzValue = byId<HTMLOutputElement>('light-az-value');
  const lightEl = byId<HTMLInputElement>('light-el');
  const lightElValue = byId<HTMLOutputElement>('light-el-value');
  const matchProportions = byId<HTMLInputElement>('match-proportions');
  const fovSlider = byId<HTMLInputElement>('fov-slider');
  const fovValue = byId<HTMLOutputElement>('fov-value');
  const fpsToggle = byId<HTMLDivElement>('fps-toggle');
  const viewMode = byId<HTMLSelectElement>('view-mode');
  const fileStatus = byId<HTMLDivElement>('file-status');
  const metadata = byId<HTMLDListElement>('metadata');
  const actorsPanel = byId<HTMLElement>('actors-panel');
  const actorList = byId<HTMLDivElement>('actor-list');
  const bgColor = byId<HTMLInputElement>('bg-color');
  const camEuler = byId<HTMLSpanElement>('cam-euler');
  const aspectSelect = byId<HTMLSelectElement>('aspect-select');
  const resolutionSelect = byId<HTMLSelectElement>('resolution-select');
  const outputDims = byId<HTMLParagraphElement>('output-dims');
  const sceneSave = byId<HTMLButtonElement>('scene-save');
  const sceneLoad = byId<HTMLButtonElement>('scene-load');
  const sceneFileInput = byId<HTMLInputElement>('scene-file-input');
  const videoPanel = byId<HTMLElement>('video-panel');
  const videoName = byId<HTMLDivElement>('video-name');
  const videoLayout = byId<HTMLSelectElement>('video-layout');
  const videoDelta = byId<HTMLInputElement>('video-delta');
  const overlayControls = byId<HTMLDivElement>('overlay-controls');
  const videoOnTop = byId<HTMLInputElement>('video-ontop');
  const videoKeyBg = byId<HTMLInputElement>('video-keybg');
  const videoExportComposite = byId<HTMLInputElement>('video-export-composite');
  const videoOpacity = byId<HTMLInputElement>('video-opacity');
  const sceneOpacity = byId<HTMLInputElement>('scene-opacity');
  const videoRemove = byId<HTMLButtonElement>('video-remove');
  const actorAdjust = byId<HTMLDivElement>('actor-adjust');
  const adjustActor = byId<HTMLSelectElement>('adjust-actor');
  const adjustX = byId<HTMLInputElement>('adjust-x');
  const adjustY = byId<HTMLInputElement>('adjust-y');
  const adjustZ = byId<HTMLInputElement>('adjust-z');
  const adjustScale = byId<HTMLInputElement>('adjust-scale');
  const adjustScaleValue = byId<HTMLOutputElement>('adjust-scale-value');
  const adjustReset = byId<HTMLButtonElement>('adjust-reset');

  /** Rows from the last setActors call (drives the Adjust panel refill). */
  let actorRows: ActorRow[] = [];

  const ftuneVrm0 = byId<HTMLInputElement>('ftune-vrm0');
  const ftuneSliders: { el: HTMLInputElement; out: HTMLOutputElement; key: string }[] = [
    { el: byId('ftune-thumbout'), out: byId('ftune-thumbout-value'), key: 'thumbOut' },
    { el: byId('ftune-thumbroll'), out: byId('ftune-thumbroll-value'), key: 'thumbRoll' },
    { el: byId('ftune-thumbcurl'), out: byId('ftune-thumbcurl-value'), key: 'thumbCurl' },
    { el: byId('ftune-curl'), out: byId('ftune-curl-value'), key: 'fingerCurl' },
    { el: byId('ftune-splay'), out: byId('ftune-splay-value'), key: 'fingerSplay' },
    { el: byId('ftune-flipl'), out: byId('ftune-flipl-value'), key: 'handFlipLeft' },
    { el: byId('ftune-turnl'), out: byId('ftune-turnl-value'), key: 'handTurnLeft' },
    { el: byId('ftune-flipr'), out: byId('ftune-flipr-value'), key: 'handFlipRight' },
    { el: byId('ftune-turnr'), out: byId('ftune-turnr-value'), key: 'handTurnRight' },
  ];
  ftuneVrm0.addEventListener('change', () => {
    if (adjustActor.value) handlers.onFingerTune(adjustActor.value, { vrm0Fix: ftuneVrm0.checked });
  });
  for (const s of ftuneSliders) {
    s.el.addEventListener('input', () => {
      s.out.value = s.el.value;
      if (adjustActor.value) {
        handlers.onFingerTune(adjustActor.value, { [s.key]: Number(s.el.value) });
      }
    });
  }
  byId<HTMLButtonElement>('ftune-key').addEventListener('click', () => {
    if (adjustActor.value) handlers.onFingerKey(adjustActor.value);
  });

  function fillAdjustInputs(): void {
    const row = actorRows.find((r) => r.id === adjustActor.value);
    // "None" (or a stale id) disables the editors instead of showing stale values.
    const disabled = !row;
    for (const el of [adjustX, adjustY, adjustZ, adjustScale, adjustReset]) el.disabled = disabled;
    byId<HTMLButtonElement>('adjust-key').disabled = disabled;
    if (!row) return;
    adjustX.value = String(row.adjust.x);
    adjustY.value = String(row.adjust.y);
    adjustZ.value = String(row.adjust.z);
    adjustScale.value = String(Math.round(row.adjust.scale * 100));
    adjustScaleValue.value = adjustScale.value;

    ftuneVrm0.checked = row.fingerTune.vrm0Fix;
    for (const s of ftuneSliders) {
      const value = row.fingerTune[s.key as keyof ActorRow['fingerTune']];
      if (document.activeElement !== s.el) {
        s.el.value = String(Math.round(value as number));
        s.out.value = s.el.value;
      }
    }
  }

  adjustActor.addEventListener('change', () => {
    fillAdjustInputs();
    handlers.onAdjustSelect(); // gizmos re-read the selection on notify
  });
  const emitAdjust = (): void => {
    if (!adjustActor.value) return;
    handlers.onAdjustChange(adjustActor.value, {
      x: Number(adjustX.value) || 0,
      y: Number(adjustY.value) || 0,
      z: Number(adjustZ.value) || 0,
      scale: (Number(adjustScale.value) || 100) / 100,
    });
    // Keep the cached row in sync so re-selects show the live values.
    const row = actorRows.find((r) => r.id === adjustActor.value);
    if (row) {
      row.adjust = {
        x: Number(adjustX.value) || 0,
        y: Number(adjustY.value) || 0,
        z: Number(adjustZ.value) || 0,
        scale: (Number(adjustScale.value) || 100) / 100,
      };
    }
  };
  for (const input of [adjustX, adjustY, adjustZ]) input.addEventListener('input', emitAdjust);
  adjustScale.addEventListener('input', () => {
    adjustScaleValue.value = adjustScale.value;
    emitAdjust();
  });
  adjustReset.addEventListener('click', () => {
    if (adjustActor.value) handlers.onAdjustReset(adjustActor.value);
  });

  const adjustKeyBtn = byId<HTMLButtonElement>('adjust-key');
  const adjustKeysList = byId<HTMLDivElement>('adjust-keys');
  let adjustKeysSignature = '';
  adjustKeyBtn.addEventListener('click', () => {
    if (adjustActor.value) handlers.onAdjustKey(adjustActor.value);
  });

  function rebuildAdjustKeys(): void {
    const row = actorRows.find((r) => r.id === adjustActor.value);
    adjustKeysList.innerHTML = '';
    if (!row) return;
    row.adjustKeys.forEach((k, index) => {
      const el = document.createElement('div');
      el.className = 'contact-key-row';
      const label = document.createElement('span');
      label.textContent = `${k.time.toFixed(2)}s → (${k.x}, ${k.y}, ${k.z}) · ${Math.round(k.scale * 100)}%`;
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'btn icon small';
      del.innerHTML = '&times;';
      del.title = 'Delete offset key';
      del.addEventListener('click', () => handlers.onAdjustKeyDelete(row.id, index));
      el.append(label, del);
      adjustKeysList.appendChild(el);
    });
  }

  const fingerFix = byId<HTMLInputElement>('finger-fix');
  const fingerFixValue = byId<HTMLOutputElement>('finger-fix-value');
  fingerFix.addEventListener('input', () => {
    fingerFixValue.value = fingerFix.value;
    handlers.onFingerFix(Number(fingerFix.value) / 100);
  });

  gridToggle.addEventListener('change', () => handlers.onToggleGrid(gridToggle.checked));
  shadowToggle.addEventListener('change', () => handlers.onGroundShadow(shadowToggle.checked));
  shadowOpacity.addEventListener('input', () => {
    shadowOpacityValue.value = shadowOpacity.value;
    handlers.onShadowOpacity(Number(shadowOpacity.value) / 100);
  });
  const emitLightAngles = (): void => {
    lightAzValue.value = lightAz.value;
    lightElValue.value = lightEl.value;
    handlers.onLightAngles(Number(lightAz.value), Number(lightEl.value));
  };
  lightAz.addEventListener('input', emitLightAngles);
  lightEl.addEventListener('input', emitLightAngles);
  matchProportions.addEventListener('change', () =>
    handlers.onMatchProportions(matchProportions.checked)
  );

  fovSlider.addEventListener('input', () => {
    const fov = Number(fovSlider.value);
    fovValue.value = String(fov);
    handlers.onFov(fov);
  });

  fpsToggle.querySelectorAll<HTMLButtonElement>('.seg').forEach((btn) => {
    btn.addEventListener('click', () => {
      const fps = Number(btn.dataset.fps) as Fps;
      handlers.onFps(fps);
    });
  });

  viewMode.addEventListener('change', () => handlers.onViewMode(viewMode.value as ViewMode));

  bgColor.addEventListener('input', () => handlers.onBackground(bgColor.value));
  aspectSelect.addEventListener('change', () =>
    handlers.onAspect(aspectSelect.value as AspectKey)
  );
  resolutionSelect.addEventListener('change', () =>
    handlers.onResolution(resolutionSelect.value as ResolutionKey)
  );
  videoLayout.addEventListener('change', () =>
    handlers.onVideoLayout(videoLayout.value as VideoLayout)
  );
  videoDelta.addEventListener('change', () => handlers.onVideoDelta(Number(videoDelta.value)));
  videoDelta.addEventListener('input', () => handlers.onVideoDelta(Number(videoDelta.value)));
  videoOnTop.addEventListener('change', () => handlers.onVideoOnTop(videoOnTop.checked));
  videoKeyBg.addEventListener('change', () => handlers.onVideoKeyBg(videoKeyBg.checked));
  videoExportComposite.addEventListener('change', () =>
    handlers.onVideoExportComposite(videoExportComposite.checked)
  );
  videoOpacity.addEventListener('input', () => handlers.onVideoOpacity(Number(videoOpacity.value)));
  sceneOpacity.addEventListener('input', () => handlers.onSceneOpacity(Number(sceneOpacity.value)));
  videoRemove.addEventListener('click', () => handlers.onRemoveVideo());

  sceneSave.addEventListener('click', () => handlers.onSaveScene());
  sceneLoad.addEventListener('click', () => sceneFileInput.click());
  sceneFileInput.addEventListener('change', () => {
    const file = sceneFileInput.files?.[0];
    if (file) handlers.onLoadScene(file);
    sceneFileInput.value = ''; // allow re-loading the same file
  });

  byId<HTMLButtonElement>('preview-xbot').addEventListener('click', () =>
    handlers.onPreviewMesh('xbot')
  );
  byId<HTMLButtonElement>('preview-ybot').addEventListener('click', () =>
    handlers.onPreviewMesh('ybot')
  );

  function setFile(meta: FileMeta | null): void {
    if (!meta) {
      fileStatus.textContent = 'No file loaded';
      fileStatus.classList.remove('loaded');
      metadata.innerHTML = '';
      return;
    }
    fileStatus.textContent = meta.name;
    fileStatus.classList.add('loaded');
    const actorsRow = meta.actors > 1 ? `<dt>Actors</dt><dd>${meta.actors}</dd>` : '';
    const rateRow = meta.nativeFps ? `<dt>Rate</dt><dd>${meta.nativeFps} fps</dd>` : '';
    metadata.innerHTML = `
      <dt>Type</dt><dd>${meta.hasMesh ? 'Mesh + rig' : 'Bone-only'}</dd>
      ${actorsRow}
      <dt>Bones</dt><dd>${meta.bones}</dd>
      <dt>Frames</dt><dd>${meta.frames}</dd>
      <dt>Duration</dt><dd>${meta.duration.toFixed(2)}s</dd>
      ${rateRow}
    `;
  }

  function setPuppetAvailable(available: boolean): void {
    const meshOption = viewMode.querySelector<HTMLOptionElement>('option[value="mesh"]');
    if (meshOption) meshOption.disabled = !available;
  }

  /** Render the per-item show/hide list (Motive-style: Skeleton 001, Rigid Body 001). */
  function setActors(rows: ActorRow[]): void {
    actorRows = rows;
    actorsPanel.hidden = rows.length === 0;
    actorList.innerHTML = '';

    // The Adjust panel targets actors that have a puppet to move.
    const adjustable = rows.filter((r) => r.mappable);
    actorAdjust.hidden = adjustable.length === 0;
    const previous = adjustActor.value;
    adjustActor.innerHTML = '';
    // "None" hides the offset gizmo + deviation lines for a clean preview.
    const none = document.createElement('option');
    none.value = '';
    none.textContent = 'None (hide gizmos)';
    adjustActor.appendChild(none);
    for (const r of adjustable) {
      const opt = document.createElement('option');
      opt.value = r.id;
      opt.textContent = r.avatar ? `${r.label} (${r.avatar})` : r.label;
      adjustActor.appendChild(opt);
    }
    // Keep the prior selection (including an explicit None); default to the
    // first actor so the gizmo still appears out of the box.
    adjustActor.value =
      previous === '' || adjustable.some((r) => r.id === previous)
        ? previous
        : (adjustable[0]?.id ?? '');
    if (adjustable.length > 0) fillAdjustInputs();
    for (const row of rows) {
      const label = document.createElement('label');
      label.className = 'actor-row';

      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.checked = row.visible;
      cb.addEventListener('change', () => handlers.onToggleActor(row.id, cb.checked));

      const text = document.createElement('span');
      text.className = `actor-name ${row.kind}`;
      text.textContent = row.label;
      if (row.avatar) text.title = `Avatar: ${row.avatar}`;

      label.append(cb, text);

      // Per-actor VRM avatar button (skeletons only).
      if (row.kind === 'skeleton') {
        const avatarBtn = document.createElement('button');
        avatarBtn.type = 'button';
        avatarBtn.className = 'btn small actor-avatar';
        avatarBtn.textContent = row.avatar ? 'VRM ✓' : 'VRM…';
        avatarBtn.classList.toggle('assigned', row.avatar !== null);
        avatarBtn.title = row.avatar
          ? `Avatar: ${row.avatar} — click to replace`
          : 'Load a VRM avatar for this actor';
        avatarBtn.addEventListener('click', (e) => {
          e.preventDefault(); // don't toggle the visibility checkbox label
          handlers.onLoadAvatar(row.id);
        });
        label.appendChild(avatarBtn);

        if (row.mappable) {
          const mapBtn = document.createElement('button');
          mapBtn.type = 'button';
          mapBtn.className = 'btn small actor-avatar';
          mapBtn.innerHTML = '&#9881;';
          mapBtn.title = 'Bone mapping…';
          mapBtn.addEventListener('click', (e) => {
            e.preventDefault();
            handlers.onMapBones(row.id);
          });
          label.appendChild(mapBtn);
        }
      }

      // Per-actor colour swatch (skeletons only).
      if (row.color !== null) {
        const swatch = document.createElement('input');
        swatch.type = 'color';
        swatch.className = 'actor-color';
        swatch.value = row.color;
        swatch.title = 'Actor colour';
        swatch.addEventListener('input', () => handlers.onActorColor(row.id, swatch.value));
        label.appendChild(swatch);
      }

      actorList.appendChild(label);
    }
  }

  /** Live camera rotation readout (degrees), driven by OrbitControls changes. */
  function setCameraInfo(euler: { x: number; y: number; z: number }): void {
    camEuler.textContent = `${Math.round(euler.x)}, ${Math.round(euler.y)}, ${Math.round(euler.z)}`;
  }

  /** Show/hide the reference-video panel and its name. */
  function setVideo(name: string | null): void {
    videoPanel.hidden = name === null;
    videoName.textContent = name ?? '';
  }

  /**
   * Keep a dynamic segment for the take's native rate (e.g. Move.AI 180) in the
   * FPS toggle when it isn't one of the static 60/120/240 buttons.
   */
  function syncNativeFpsSegment(): void {
    const existing = fpsToggle.querySelector<HTMLButtonElement>('.seg[data-native]');
    const staticRates = [...fpsToggle.querySelectorAll<HTMLButtonElement>('.seg:not([data-native])')]
      .map((b) => Number(b.dataset.fps));
    const native = state.nativeFps;
    const wanted = native !== null && !staticRates.includes(native) ? native : null;
    if (existing && Number(existing.dataset.fps) !== wanted) existing.remove();
    if (wanted !== null && Number(existing?.dataset.fps) !== wanted) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'seg';
      btn.dataset.fps = String(wanted);
      btn.dataset.native = '';
      btn.title = 'Native rate of the loaded take';
      btn.textContent = String(wanted);
      btn.addEventListener('click', () => handlers.onFps(wanted));
      // Insert in ascending rate order.
      const after = [...fpsToggle.querySelectorAll<HTMLButtonElement>('.seg')].find(
        (b) => Number(b.dataset.fps) > wanted
      );
      fpsToggle.insertBefore(btn, after ?? null);
    }
  }

  /** Reflect current state into the controls (called on every notify()). */
  function update(): void {
    // Keep FPS segmented buttons in sync.
    syncNativeFpsSegment();
    fpsToggle.querySelectorAll<HTMLButtonElement>('.seg').forEach((btn) => {
      btn.classList.toggle('active', Number(btn.dataset.fps) === state.fps);
    });
    if (viewMode.value !== state.viewMode) viewMode.value = state.viewMode;

    shadowToggle.checked = state.lighting.shadow;
    if (document.activeElement !== shadowOpacity) {
      const pct = String(Math.round(state.lighting.shadowOpacity * 100));
      shadowOpacity.value = pct;
      shadowOpacityValue.value = pct;
    }
    if (document.activeElement !== lightAz) {
      lightAz.value = String(state.lighting.azimuth);
      lightAzValue.value = String(state.lighting.azimuth);
    }
    if (document.activeElement !== lightEl) {
      lightEl.value = String(state.lighting.elevation);
      lightElValue.value = String(state.lighting.elevation);
    }
    matchProportions.checked = state.matchProportions;

    // The FOV slider drives the free camera only; animated views keyframe fov.
    fovSlider.disabled = state.cameraView !== 'free';

    if (document.activeElement !== bgColor) {
      bgColor.value = `#${state.background.toString(16).padStart(6, '0')}`;
    }
    aspectSelect.value = state.aspect;
    resolutionSelect.value = state.resolution;
    resolutionSelect.disabled = state.aspect === 'free';

    const dims = exportDimensions(state.aspect, state.resolution);
    outputDims.textContent = dims
      ? `MP4 export: ${dims.width}×${dims.height} (${state.aspect})`
      : 'Video export uses the viewport size.';

    sceneSave.disabled = state.source === null;

    if (document.activeElement !== fingerFix) {
      const pct = String(Math.round(state.fingerFix * 100));
      fingerFix.value = pct;
      fingerFixValue.value = pct;
    }

    // Keyframed offsets: reflect the live evaluated values while scrubbing/playing
    // and keep the key list in sync.
    if (!actorAdjust.hidden && adjustActor.value) {
      const row = actorRows.find((r) => r.id === adjustActor.value);
      if (row && row.adjustKeys.length > 0) {
        const live = handlers.getAdjustLive(row.id);
        const active = document.activeElement;
        if (live && active !== adjustX && active !== adjustY && active !== adjustZ && active !== adjustScale) {
          adjustX.value = String(+live.x.toFixed(3));
          adjustY.value = String(+live.y.toFixed(3));
          adjustZ.value = String(+live.z.toFixed(3));
          adjustScale.value = String(Math.round(live.scale * 100));
          adjustScaleValue.value = adjustScale.value;
        }
      }
      const signature = JSON.stringify([adjustActor.value, row?.adjustKeys]);
      if (signature !== adjustKeysSignature) {
        adjustKeysSignature = signature;
        rebuildAdjustKeys();
      }

      // Keyframed finger tunes: reflect the live evaluated values too.
      if (row && row.fingerKeyCount > 0) {
        const live = handlers.getFingerTuneLive(row.id);
        if (live) {
          for (const s of ftuneSliders) {
            if (document.activeElement === s.el) continue;
            const value = live[s.key as keyof FingerTune];
            s.el.value = String(Math.round(value as number));
            s.out.value = s.el.value;
          }
        }
      }
    }

    // Reference-video controls.
    const v = state.video;
    if (videoLayout.value !== v.layout) videoLayout.value = v.layout;
    if (document.activeElement !== videoDelta) videoDelta.value = String(v.frameDelta);
    overlayControls.hidden = v.layout !== 'overlay';
    videoOnTop.checked = v.onTop;
    videoKeyBg.checked = v.keyBackground;
    videoKeyBg.disabled = v.onTop; // only meaningful with the 3D on top
    videoExportComposite.checked = v.includeInExport;
    videoOpacity.value = String(v.opacity);
    sceneOpacity.value = String(v.sceneOpacity);
  }

  /** The actor currently shown in the Adjust panel (offset gizmo target). */
  function getAdjustActor(): string | null {
    return actorAdjust.hidden ? null : adjustActor.value || null;
  }

  return { setFile, setPuppetAvailable, setActors, setCameraInfo, setVideo, getAdjustActor, update };
}

function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}
